import { AxiosError } from 'axios';
import { api } from './api';
import type {
  AiConversationStatsScope,
  ApiResponse,
  AIProfileType,
  CapabilityClass,
  NimModelCatalog,
  AutodevEstimateRecord as AutodevEstimateView,
  AutodevEstimateQuotaSnapshot as AutodevEstimateQuotaView,
} from '@elmes/shared';

export type { AutodevEstimateView, AutodevEstimateQuotaView };

/** GET /ai/conversations/stats — `scope` says whose conversations were counted (S7d-5b). */
interface ConversationStats {
  total: number | string;
  active: number | string | null;
  closed: number | string | null;
  total_messages: number | string | null;
  total_tokens: number | string | null;
  scope: AiConversationStatsScope;
}

export interface RoutingMetricsSnapshot {
  totalCalls: number;
  fallback: { count: number; rate: number | null; byReason: Record<string, number> };
  toolCount: { p50: number | null; p95: number | null; mean: number | null; samples: number };
  routerDurationMs: { p50: number | null; p95: number | null; mean: number | null; samples: number };
  miscategorized: { total: number; perCallMean: number | null };
  cache: { hitRatio: number | null; readTokens: number; creationTokens: number; reportingCalls: number };
  router: { inputTokens: number; outputTokens: number };
  ringSize: number;
}

// --- Tools ---
export const aiService = {
  async getTools(category?: string) {
    const params = category ? { category } : {};
    const { data } = await api.get<ApiResponse<{ tools: unknown[]; categories: string[] }>>('/ai/tools', { params });
    return data.data!;
  },

  async executeTool(tool: string, params?: Record<string, unknown>) {
    const { data } = await api.post<ApiResponse<unknown>>('/ai/execute', { tool, params });
    return data.data;
  },

  // Confirm or cancel a paused destructive tool (roadmap #48 PR-B).
  // Resolves true if the registry accepted the ack; false if the entry was
  // already resolved or never existed (e.g. server restart, timeout).
  async confirmTool(correlationId: string, confirm: boolean): Promise<boolean> {
    try {
      const { data } = await api.post<ApiResponse<{ correlationId: string; confirmed: boolean }>>(
        `/ai/tool-confirm/${encodeURIComponent(correlationId)}`,
        { confirm },
      );
      return !!data.success;
    } catch {
      return false;
    }
  },

  async getStatus() {
    const { data } = await api.get<ApiResponse<unknown>>('/ai/status');
    return data.data;
  },

  // --- Operational Memory ---
  async listMemory(category?: string) {
    const params = category ? { category } : {};
    const { data } = await api.get<ApiResponse<unknown[]>>('/ai/memory', { params });
    return data.data!;
  },

  async getMemory(key: string) {
    const { data } = await api.get<ApiResponse<unknown>>(`/ai/memory/${encodeURIComponent(key)}`);
    return data.data;
  },

  async setMemory(key: string, value: Record<string, unknown>) {
    const { data } = await api.put<ApiResponse<unknown>>(`/ai/memory/${encodeURIComponent(key)}`, value);
    return data.data;
  },

  async deleteMemory(key: string) {
    await api.delete(`/ai/memory/${encodeURIComponent(key)}`);
  },

  async getMemoryStats() {
    const { data } = await api.get<ApiResponse<unknown>>('/ai/memory/stats');
    return data.data;
  },

  // --- Conversations ---
  async listConversations(status?: string, profileId?: number) {
    const params: Record<string, string | number> = {};
    if (status) params.status = status;
    if (profileId) params.profileId = profileId;
    const { data } = await api.get<ApiResponse<unknown[]>>('/ai/conversations', { params });
    return data.data!;
  },

  async createConversation(title: string, context?: Record<string, unknown>) {
    const { data } = await api.post<ApiResponse<{ id: number; title: string }>>('/ai/conversations', { title, context });
    return data.data!;
  },

  async getConversation(id: number) {
    const { data } = await api.get<ApiResponse<{ conversation: unknown; messages: unknown[] }>>(`/ai/conversations/${id}`);
    return data.data!;
  },

  async addMessage(conversationId: number, role: string, content: string) {
    const { data } = await api.post<ApiResponse<unknown>>(`/ai/conversations/${conversationId}/messages`, { role, content, tokens_used: content.length });
    return data.data;
  },

  async closeConversation(id: number) {
    const { data } = await api.patch<ApiResponse<unknown>>(`/ai/conversations/${id}/close`);
    return data.data;
  },

  async getConversationStats() {
    const { data } = await api.get<ApiResponse<ConversationStats>>('/ai/conversations/stats');
    return data.data;
  },

  // --- Tool routing metrics (#59 / PR-C) ---
  async getRoutingMetrics() {
    const { data } = await api.get<ApiResponse<RoutingMetricsSnapshot>>('/ai/routing-metrics');
    return data.data;
  },

  async resetRoutingMetrics() {
    const { data } = await api.post<ApiResponse<{ reset: boolean }>>('/ai/routing-metrics/reset');
    return data.data;
  },

  // --- Message Summaries ---
  async getMessageSummary(messageId: number) {
    const { data } = await api.get<ApiResponse<unknown>>(`/ai/messages/${messageId}/summary`);
    return data.data;
  },

  async generateMessageSummary(messageId: number, profileId?: number) {
    const body = profileId ? { profileId } : {};
    const { data } = await api.post<ApiResponse<unknown>>(`/ai/messages/${messageId}/summary`, body);
    return data.data;
  },

  async deleteMessageSummary(messageId: number) {
    await api.delete(`/ai/messages/${messageId}/summary`);
  },

  async batchGenerateSummaries(conversationId: number, profileId?: number) {
    const body = profileId ? { profileId } : {};
    const { data } = await api.post<ApiResponse<{ generated: number; skipped: number; total: number }>>(`/ai/conversations/${conversationId}/summaries/batch`, body);
    return data.data!;
  },

  async getConversationSummaryStats(conversationId: number) {
    const { data } = await api.get<ApiResponse<unknown>>(`/ai/conversations/${conversationId}/summaries/stats`);
    return data.data;
  },

  async submitSummaryFeedback(messageId: number, rating: 'up' | 'down', note?: string) {
    const body: Record<string, unknown> = { rating };
    if (note) body.note = note;
    const { data } = await api.post<ApiResponse<unknown>>(`/ai/messages/${messageId}/summary/feedback`, body);
    return data.data;
  },

  async searchSummaries(query: string, limit?: number) {
    const params: Record<string, string | number> = { q: query };
    if (limit) params.limit = limit;
    const { data } = await api.get<ApiResponse<unknown[]>>('/ai/summaries/search', { params });
    return data.data!;
  },

  async batchSummarizeAll(profileId?: number) {
    const body = profileId ? { profileId } : {};
    const { data } = await api.post<ApiResponse<{ conversationsProcessed: number; generated: number; skipped: number }>>('/ai/summaries/batch-all', body);
    return data.data!;
  },

  // --- Tasks ---
  async listTasks(status?: string) {
    const params = status ? { status } : {};
    const { data } = await api.get<ApiResponse<unknown[]>>('/ai/tasks', { params });
    return data.data!;
  },

  async createTask(task: { type: string; description: string; params?: Record<string, unknown> }) {
    const { data } = await api.post<ApiResponse<unknown>>('/ai/tasks', task);
    return data.data;
  },

  async getTask(id: number) {
    const { data } = await api.get<ApiResponse<unknown>>(`/ai/tasks/${id}`);
    return data.data;
  },

  async updateTaskStatus(id: number, status: string, result?: unknown, errorMessage?: string) {
    const body: Record<string, unknown> = { status };
    if (result !== undefined) body.result = result;
    if (errorMessage) body.error_message = errorMessage;
    const { data } = await api.patch<ApiResponse<unknown>>(`/ai/tasks/${id}/status`, body);
    return data.data;
  },

  async getTaskStats() {
    const { data } = await api.get<ApiResponse<unknown>>('/ai/tasks/stats');
    return data.data;
  },

  // --- Settings ---
  async getSettings() {
    const { data } = await api.get<ApiResponse<AISettings>>('/ai/settings');
    return data.data!;
  },

  async updateSettings(settings: AISettingsUpdate) {
    const { data } = await api.put<ApiResponse<AISettings>>('/ai/settings', settings);
    return data.data!;
  },

  /**
   * Live NVIDIA NIM (hosted) catalogue. Admin-gated — a non-admin gets 403 and
   * the caller is expected to fall back to the bundled list.
   *
   * Returns the ENVELOPE, not a bare array: `source`, `stale` and each row's
   * `listed` flag are what let the editor say where the list came from and
   * mark ids NVIDIA no longer serves. The only catalogue route (#2603).
   */
  async getNimModels(refresh = false) {
    const params = refresh ? { refresh: 'true' } : undefined;
    const { data } = await api.get<ApiResponse<NimModelCatalog>>('/ai/providers/nim/models', { params });
    return data.data!;
  },

  // --- User Context ---
  async getUserContext() {
    const { data } = await api.get<ApiResponse<UserContextData>>('/ai/context/me');
    return data.data!;
  },

  // --- Tool Registry (DB-backed, #293) ---
  async listTools(params?: {
    search?: string;
    category?: string;
    plugin?: string;
    danger?: string;
    page?: number;
    limit?: number;
  }): Promise<{ items: AiTool[]; total: number; page: number; limit: number }> {
    const qs = new URLSearchParams();
    if (params?.search) qs.set('search', params.search);
    if (params?.category) qs.set('category', params.category);
    if (params?.plugin) qs.set('plugin', params.plugin);
    if (params?.danger) qs.set('danger', params.danger);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    const { data } = await api.get<{ items: AiTool[]; total: number; page: number; limit: number }>(`/ai/tools/v2?${qs}`);
    return data;
  },

  // --- Profiles ---
  async listProfiles(params?: { page?: number; limit?: number; type?: string; visibility?: string; isActive?: string; isTemplate?: string; search?: string }) {
    const { data } = await api.get<{ success: boolean; data: AIProfile[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>('/ai/profiles', { params });
    return { data: data.data, pagination: data.pagination };
  },

  async getProfile(id: number) {
    const { data } = await api.get<ApiResponse<AIProfile>>(`/ai/profiles/${id}`);
    return data.data!;
  },

  /** `null` when the slug is absent or not accessible — the API folds both into one 404 (gh#2879). */
  async getProfileBySlug(slug: string): Promise<AIProfile | null> {
    try {
      const { data } = await api.get<ApiResponse<AIProfile>>(`/ai/profiles/by-slug/${encodeURIComponent(slug)}`);
      return data.data ?? null;
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === 404) return null;
      throw err;
    }
  },

  async createProfile(body: Record<string, unknown>) {
    const { data } = await api.post<ApiResponse<AIProfile>>('/ai/profiles', body);
    return data.data!;
  },

  async updateProfile(id: number, body: Record<string, unknown>) {
    const { data } = await api.put<ApiResponse<AIProfile>>(`/ai/profiles/${id}`, body);
    return data.data!;
  },

  async deleteProfile(id: number) {
    const { data } = await api.delete<ApiResponse<{ deleted: boolean }>>(`/ai/profiles/${id}`);
    return data.data!;
  },

  async duplicateProfile(id: number) {
    const { data } = await api.post<ApiResponse<AIProfile>>(`/ai/profiles/${id}/duplicate`);
    return data.data!;
  },

  async createFromTemplate(templateId: number, overrides: Record<string, unknown>) {
    const { data } = await api.post<ApiResponse<AIProfile>>(`/ai/profiles/${templateId}/from-template`, overrides);
    return data.data!;
  },

  async getTemplates(category?: string) {
    const params = category ? { category } : {};
    const { data } = await api.get<ApiResponse<AIProfile[]>>('/ai/profiles/templates', { params });
    return data.data!;
  },

  async getMyProfiles() {
    const { data } = await api.get<ApiResponse<AIProfile[]>>('/ai/profiles/me');
    return data.data!;
  },

  async getProfileWorkspace(id: number) {
    const { data } = await api.get<ApiResponse<{ stats: unknown; files: unknown[] }>>(`/ai/profiles/${id}/workspace`);
    return data.data!;
  },

  async exportProfile(id: number) {
    const { data } = await api.get<ApiResponse<unknown>>(`/ai/profiles/${id}/export`);
    return data.data!;
  },

  async importProfile(jsonBlob: unknown) {
    const { data } = await api.post<ApiResponse<AIProfile>>('/ai/profiles/import', jsonBlob);
    return data.data!;
  },

  async getProfileQuota(id: number) {
    const { data } = await api.get<ApiResponse<ProfileQuota>>(`/ai/profiles/${id}/quota`);
    return data.data!;
  },

  async resetProfileQuota(id: number) {
    const { data } = await api.post<ApiResponse<unknown>>(`/ai/profiles/${id}/quota/reset`);
    return data.data!;
  },

  // --- Audit Log ---
  async getAuditLog(params?: { limit?: number; agentId?: number }) {
    const { data } = await api.get<ApiResponse<unknown[]>>('/ai/audit', { params });
    return data.data!;
  },

  // --- Issues ---
  async listIssues(params?: Record<string, unknown>) {
    const { data } = await api.get<ApiResponse<unknown[]> & { pagination: unknown }>('/ai/issues', { params });
    return { data: data.data!, pagination: (data as unknown as Record<string, unknown>).pagination };
  },

  async listIssueRevisions(id: number) {
    const { data } = await api.get<ApiResponse<unknown[]>>(`/ai/issues/${id}/revisions`);
    return data.data ?? [];
  },

  async getIssue(id: number) {
    const { data } = await api.get<ApiResponse<unknown>>(`/ai/issues/${id}`);
    return data.data!;
  },

  async createIssue(body: Record<string, unknown>) {
    const { data } = await api.post<ApiResponse<unknown>>('/ai/issues', body);
    return data.data!;
  },

  async updateIssue(id: number, body: Record<string, unknown>) {
    const { data } = await api.put<ApiResponse<unknown>>(`/ai/issues/${id}`, body);
    return data.data!;
  },

  async deleteIssue(id: number) {
    await api.delete(`/ai/issues/${id}`);
  },

  async getIssueStats() {
    const { data } = await api.get<ApiResponse<unknown>>('/ai/issues/stats');
    return data.data!;
  },

  async getDevStats() {
    const { data } = await api.get<ApiResponse<unknown>>('/ai/issues/dev-stats');
    return data.data as
      | { mode: 'sync'; queueEnabled: false }
      | {
          mode: 'queued';
          queueEnabled: true;
          concurrency: number;
          counts: { active: number; waiting: number; delayed: number; completed: number; failed: number };
        };
  },

  async getDlqIssues() {
    const { data } = await api.get<ApiResponse<unknown>>('/ai/issues/dlq');
    return data.data as {
      issues: Array<{
        id: number;
        title: string;
        type: string;
        priority: string;
        status: string;
        devStatus: string | null;
        devAttempt: number | null;
        devError: string | null;
        devBranch: string | null;
        devPrUrl: string | null;
        updatedAt: string | null;
      }>;
      maxAttempts: number;
      count: number;
    };
  },

  async dlqRetry(id: number) {
    const { data } = await api.post<ApiResponse<unknown>>(`/ai/issues/${id}/dev/dlq-retry`);
    return data.data as { mode: 'sync' | 'queued'; jobId?: string; alreadyEnqueued?: boolean };
  },

  async assignIssue(id: number, body: Record<string, unknown>) {
    const { data } = await api.patch<ApiResponse<unknown>>(`/ai/issues/${id}/assign`, body);
    return data.data!;
  },

  async reviewIssue(id: number, profileId?: number) {
    const { data } = await api.post<ApiResponse<unknown>>(`/ai/issues/${id}/review`, profileId ? { profileId } : {});
    return data;
  },

  async developIssue(id: number, profileId?: number, force?: boolean) {
    const body: Record<string, unknown> = {};
    if (profileId) body.profileId = profileId;
    if (force) body.force = true;
    const { data } = await api.post<ApiResponse<unknown>>(`/ai/issues/${id}/develop`, body);
    return data;
  },

  async reprocessIssueComment(issueId: number, commentId: number, profileId?: number) {
    const { data } = await api.post<ApiResponse<unknown>>(`/ai/issues/${issueId}/comments/${commentId}/reprocess`, profileId ? { profileId } : {});
    return data;
  },

  async getIssueDevLogs(issueId: number, attempt?: number) {
    const params: Record<string, unknown> = {};
    if (attempt) params.attempt = attempt;
    const { data } = await api.get<ApiResponse<unknown[]>>(`/ai/issues/${issueId}/dev-logs`, { params });
    return data.data!;
  },

  async getIssueCircuitBreaker(issueId: number) {
    const { data } = await api.get<ApiResponse<{ open: boolean; consecutiveFailures: number; threshold: number; lastResetAt: string | null }>>(
      `/ai/issues/${issueId}/dev/circuit-breaker`,
    );
    return data.data!;
  },

  async resetIssueCircuitBreaker(issueId: number) {
    const { data } = await api.post<ApiResponse<{ open: boolean; consecutiveFailures: number; threshold: number; lastResetAt: string | null }>>(
      `/ai/issues/${issueId}/dev/circuit-breaker/reset`,
    );
    return data.data!;
  },

  // Roadmap #55 — Cost-Aware Estimation
  async createIssueEstimate(issueId: number, profileId?: number) {
    const body: Record<string, unknown> = {};
    if (profileId) body.profileId = profileId;
    const { data } = await api.post<ApiResponse<{ estimate: AutodevEstimateView; quota: AutodevEstimateQuotaView }>>(
      `/ai/issues/${issueId}/estimate`,
      body,
    );
    return data.data!;
  },

  async getIssueEstimate(issueId: number) {
    const { data } = await api.get<ApiResponse<{ estimate: AutodevEstimateView; quota: AutodevEstimateQuotaView } | null>>(
      `/ai/issues/${issueId}/estimate`,
    );
    return data.data ?? null;
  },

  async approveIssueEstimate(issueId: number, estimateId: number) {
    const { data } = await api.post<ApiResponse<{ estimate: AutodevEstimateView; quota: AutodevEstimateQuotaView }>>(
      `/ai/issues/${issueId}/estimate/${estimateId}/approve`,
    );
    return data.data!;
  },

  async rejectIssueEstimate(issueId: number, estimateId: number) {
    const { data } = await api.post<ApiResponse<AutodevEstimateView>>(
      `/ai/issues/${issueId}/estimate/${estimateId}/reject`,
    );
    return data.data!;
  },

  // Issue comments
  async listIssueComments(issueId: number, params?: Record<string, unknown>) {
    const { data } = await api.get<ApiResponse<unknown[]> & { pagination: unknown }>(`/ai/issues/${issueId}/comments`, { params });
    return { data: data.data!, pagination: (data as unknown as Record<string, unknown>).pagination };
  },

  async addIssueComment(issueId: number, body: { content: string; metadata?: Record<string, unknown> }) {
    const { data } = await api.post<ApiResponse<unknown>>(`/ai/issues/${issueId}/comments`, body);
    return data.data!;
  },

  async updateIssueComment(issueId: number, commentId: number, body: { content: string }) {
    const { data } = await api.put<ApiResponse<unknown>>(`/ai/issues/${issueId}/comments/${commentId}`, body);
    return data.data!;
  },

  async deleteIssueComment(issueId: number, commentId: number) {
    await api.delete(`/ai/issues/${issueId}/comments/${commentId}`);
  },

  // Issue labels
  async listIssueLabels() {
    const { data } = await api.get<ApiResponse<unknown[]>>('/ai/issues/labels');
    return data.data!;
  },

  async createIssueLabel(body: { name: string; color?: string; description?: string }) {
    const { data } = await api.post<ApiResponse<unknown>>('/ai/issues/labels', body);
    return data.data!;
  },

  async deleteIssueLabel(id: number) {
    await api.delete(`/ai/issues/labels/${id}`);
  },

  async addLabelToIssue(issueId: number, labelId: number) {
    const { data } = await api.post<ApiResponse<unknown>>(`/ai/issues/${issueId}/labels/${labelId}`, {});
    return data.data!;
  },

  async removeLabelFromIssue(issueId: number, labelId: number) {
    await api.delete(`/ai/issues/${issueId}/labels/${labelId}`);
  },

  // PR Operations
  async getIssuePRDiff(issueId: number) {
    const { data } = await api.get<ApiResponse<{ diff: string; stats: string }>>(`/ai/issues/${issueId}/pr/diff`);
    return data.data!;
  },

  async getIssuePRStatus(issueId: number) {
    const { data } = await api.get<ApiResponse<{ state: string; mergeable: string; title: string; additions: number; deletions: number; changedFiles: number; headBranch: string; url: string }>>(`/ai/issues/${issueId}/pr/status`);
    return data.data!;
  },

  async mergeAndDeployIssue(issueId: number) {
    const { data } = await api.post<ApiResponse<unknown>>(`/ai/issues/${issueId}/pr/merge`);
    return data;
  },

  // --- Agents (F11 Sprint D) ---
  async listAgents(params?: {
    type?: string;
    parentId?: number | null;
    profileId?: number;
    tenantId?: number;
    includeDeleted?: boolean;
  }): Promise<{ items: Agent[]; total: number }> {
    const qs = new URLSearchParams();
    if (params?.type) qs.set('type', params.type);
    if (params?.parentId !== undefined) qs.set('parentId', params.parentId === null ? 'null' : String(params.parentId));
    if (params?.profileId != null) qs.set('profileId', String(params.profileId));
    if (params?.tenantId != null) qs.set('tenantId', String(params.tenantId));
    if (params?.includeDeleted) qs.set('includeDeleted', 'true');
    const { data } = await api.get<ApiResponse<{ items: Agent[]; total: number }>>(`/agents?${qs}`);
    return data.data!;
  },

  async getAgent(id: number): Promise<Agent> {
    const { data } = await api.get<ApiResponse<Agent>>(`/agents/${id}`);
    return data.data!;
  },

  async createAgent(body: CreateAgentBody): Promise<{ id: number }> {
    const { data } = await api.post<ApiResponse<{ id: number }>>('/agents', body);
    return data.data!;
  },

  async updateAgent(id: number, body: Partial<CreateAgentBody>): Promise<Agent> {
    const { data } = await api.patch<ApiResponse<Agent>>(`/agents/${id}`, body);
    return data.data!;
  },

  async deleteAgent(id: number, hard = false): Promise<void> {
    await api.delete(`/agents/${id}${hard ? '?hard=true' : ''}`);
  },

  async getAgentTree(id: number): Promise<AgentTreeNode> {
    const { data } = await api.get<ApiResponse<AgentTreeNode>>(`/agents/${id}/tree`);
    return data.data!;
  },

  async listSubAgents(id: number): Promise<{ items: Agent[]; total: number }> {
    const { data } = await api.get<ApiResponse<{ items: Agent[]; total: number }>>(`/agents/${id}/sub-agents`);
    return data.data!;
  },

  async listAgentTypes(params?: { category?: string }): Promise<AgentTypeDefinition[]> {
    const qs = new URLSearchParams();
    if (params?.category) qs.set('category', params.category);
    const { data } = await api.get<ApiResponse<{ items: AgentTypeDefinition[]; total: number }>>(`/agents/types?${qs}`);
    return data.data!.items;
  },

  async listPermissionDecisions(
    agentId: number,
    params?: {
      layer?: string;
      decision?: string;
      toolSlug?: string;
      fromDate?: string;
      toDate?: string;
      limit?: number;
      offset?: number;
    },
  ): Promise<{ items: PermissionDecision[]; total: number }> {
    const qs = new URLSearchParams();
    if (params?.layer) qs.set('layer', params.layer);
    if (params?.decision) qs.set('decision', params.decision);
    if (params?.toolSlug) qs.set('toolSlug', params.toolSlug);
    if (params?.fromDate) qs.set('fromDate', params.fromDate);
    if (params?.toDate) qs.set('toDate', params.toDate);
    if (params?.limit != null) qs.set('limit', String(params.limit));
    if (params?.offset != null) qs.set('offset', String(params.offset));
    const { data } = await api.get<ApiResponse<{ items: PermissionDecision[]; total: number }>>(
      `/agents/${agentId}/permissions/decisions?${qs}`,
    );
    return data.data!;
  },

  async getPermissionStats(agentId: number, range: '24h' | '7d' | '30d'): Promise<PermissionStats> {
    const { data } = await api.get<ApiResponse<PermissionStats>>(
      `/agents/${agentId}/permissions/stats?range=${range}`,
    );
    return data.data!;
  },

  // --- Artifact CRUD (F12 Sprint D) ---

  async listArtifacts(agentId: number, filter?: ArtifactFilter): Promise<ArtifactListResponse> {
    const params: Record<string, string | number | boolean> = {};
    if (filter?.kind) params['kind'] = filter.kind;
    if (filter?.runId) params['runId'] = filter.runId;
    if (filter?.pinned !== undefined) params['pinned'] = filter.pinned;
    if (filter?.limit !== undefined) params['limit'] = filter.limit;
    if (filter?.offset !== undefined) params['offset'] = filter.offset;
    const { data } = await api.get<ApiResponse<ArtifactListResponse>>(
      `/agents/${agentId}/artifacts`,
      { params },
    );
    return data.data!;
  },

  async getArtifact(agentId: number, artifactId: number): Promise<Artifact> {
    const { data } = await api.get<ApiResponse<Artifact>>(
      `/agents/${agentId}/artifacts/${artifactId}`,
    );
    return data.data!;
  },

  async pinArtifact(agentId: number, artifactId: number, pinned: boolean): Promise<Artifact> {
    const { data } = await api.patch<ApiResponse<Artifact>>(
      `/agents/${agentId}/artifacts/${artifactId}/pin`,
      { pinned },
    );
    return data.data!;
  },

  async deleteArtifact(agentId: number, artifactId: number): Promise<void> {
    await api.delete(`/agents/${agentId}/artifacts/${artifactId}`);
  },

  async listArtifactRunIds(agentId: number): Promise<string[]> {
    const { data } = await api.get<ApiResponse<string[]>>(
      `/agents/${agentId}/artifacts/run-ids`,
    );
    return data.data!;
  },

  // --- Agent Bindings (F15 Sprint B) ---

  async listBindingsByAgent(agentId: number): Promise<{ items: AgentBinding[]; total: number }> {
    const { data } = await api.get<ApiResponse<{ items: AgentBinding[]; total: number }>>(
      `/agents/${agentId}/bindings`,
    );
    return data.data!;
  },

  async listBindingsByTarget(targetType: string, targetId: string): Promise<{ items: AgentBinding[]; total: number }> {
    const qs = new URLSearchParams({ targetType, targetId });
    const { data } = await api.get<ApiResponse<{ items: AgentBinding[]; total: number }>>(
      `/bindings/by-target?${qs}`,
    );
    return data.data!;
  },

  async createBinding(agentId: number, body: CreateBindingBody): Promise<AgentBinding> {
    const { data } = await api.post<ApiResponse<AgentBinding>>(
      `/agents/${agentId}/bindings`,
      body,
    );
    return data.data!;
  },

  async updateBinding(agentId: number, bindingId: number, body: Partial<CreateBindingBody>): Promise<AgentBinding> {
    const { data } = await api.patch<ApiResponse<AgentBinding>>(
      `/agents/${agentId}/bindings/${bindingId}`,
      body,
    );
    return data.data!;
  },

  async deleteBinding(agentId: number, bindingId: number): Promise<void> {
    await api.delete(`/agents/${agentId}/bindings/${bindingId}`);
  },
};

export interface AISettings {
  provider: string;
  model: string;
  reasoningEffort: '' | 'low' | 'medium' | 'high' | 'xhigh';
  /** Anthropic extended thinking budget. 0 = disabled. */
  thinkingBudgetTokens: number;
  workingDirectory: string;
  timeout: number;
  maxBuffer: number;
  maxToolRounds: number;
  maxEmptyArgsRetries: number;
  autopilot: boolean;
  maxAutopilotContinues: number;
  allowAllTools: boolean;
  allowAllPaths: boolean;
  outputFormat: 'text' | 'json';
  stream: 'on' | 'off';
  customInstructionsEnabled: boolean;
  systemPrompt: string;
  responseLanguage: string;
  // Summary system
  summaryEnabled: boolean;
  summaryThreshold: number;
  summaryMaxLength: number;
  // Issue review
  issueReviewProfileId: number | null;
  issueAutoCloseRejected?: boolean;
  // Issue auto-development
  issueDevEnabled: boolean;
  issueDevProfileId: number | null;
  autoDevAfterReview?: boolean;
  // Comment review
  commentReviewEnabled?: boolean;
  commentReviewProfileId?: number | null;
  commentRedevCooldownSec?: number;
  commentMaxRedevPerHour?: number;
  // GitHub integration — the PAT is write-only (gh#2586): reads carry presence + timestamp only.
  hasGithubToken: boolean;
  githubTokenUpdatedAt: string | null;
}

/**
 * PUT body. The two status fields are server-derived and not settable; the PAT travels
 * as its own write-only field (`githubToken` sets, `clearGithubToken: true` removes).
 */
export type AISettingsUpdate = Partial<Omit<AISettings, 'hasGithubToken' | 'githubTokenUpdatedAt'>> & {
  githubToken?: string;
  clearGithubToken?: boolean;
};

export interface MemoryEntry {
  id: number;
  key: string;
  value: string;
  summary: string | null;
  category: string;
  tags: string | null;
  priority: number;
  estTokens: number;
  createdAt: string;
  updatedAt: string;
}

export interface UserContextData {
  profile: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
  } | null;
  mandatoryMemories: MemoryEntry[];
  preferences: MemoryEntry[];
  stats: Record<string, unknown>;
}

export interface AIProfile {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  avatarEmoji: string | null;
  color: string | null;
  type: AIProfileType;
  createdBy: number | null;
  isSystem: boolean;
  isActive: boolean;
  visibility: 'global' | 'personal';
  settings: Record<string, unknown>;
  permissions: Record<string, unknown>;
  // Server-side class (#2606) — read-only on this wire; the editor derives its disclosure from it.
  capabilityClass?: CapabilityClass | null;
  maxDailyTokens: number | null;
  maxDailyConversations: number | null;
  maxConcurrentRequests: number | null;
  dailyTokensUsed: number;
  dailyConversationsUsed: number;
  quotaResetAt: string | null;
  triggers: Record<string, unknown> | null;
  isTemplate: boolean;
  templateCategory: string | null;
  hasApiKey: boolean;
  /** gh#2677 — picker reads (list, `/me`, `/:id`, `/by-slug/:slug`): the model the server gate defaults to ('' = none). */
  effectiveModel?: string;
  lastUsedAt: string | null;
  conversationCount: number;
  totalTokensUsed: number;
  // Roadmap #41 PR-B (eval flags)
  evalEnabled?: boolean;
  evalJudgeProfileId?: number | null;
  canaryModelAlias?: string | null;
  createdAt: string;
  updatedAt: string;
}

// --- AI Tool Registry (#293) ---

export interface AiToolParameter {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'object' | 'array';
  description: string;
  required?: boolean;
  default?: unknown;
  enum?: string[];
}

export interface AiTool {
  id: number;
  name: string;
  description: string | null;
  category: string | null;
  parameters: AiToolParameter[] | null;
  requiredPermission: string | null;
  dangerLevel: string | null;
  costEstimate: number | null;
  latencyClass: string | null;
  idempotent: boolean | null;
  pluginSlug: string | null;
  alwaysAllowed: boolean | null;
  isActive: boolean;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

// --- Agent Studio types (F11 Sprint D) ---

export interface Agent {
  id: number;
  name: string;
  slug: string;
  type: string;
  description: string | null;
  persona: string | null;
  promptFields: Record<string, unknown> | null;
  parentAgentId: number | null;
  profileId: number | null;
  toolWhitelist: string[] | null;
  config: Record<string, unknown> | null;
  tenantId: number | null;
  createdBy: number | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface AgentTreeNode {
  id: number;
  name: string;
  slug: string;
  type: string;
  profileId: number | null;
  children: AgentTreeNode[];
}

export interface CreateAgentBody {
  name: string;
  slug: string;
  type: string;
  description?: string;
  persona?: string;
  promptFields?: Record<string, unknown>;
  parentAgentId?: number | null;
  profileId?: number | null;
  toolWhitelist?: string[];
  config?: Record<string, unknown>;
  tenantId?: number;
}

export interface AgentTypeDefinition {
  slug: string;
  label: string;
  category: 'conversation' | 'reasoning' | 'ui' | 'developer' | 'workflow' | 'meta';
  description: string;
  defaultPersonaSnippet?: string;
  defaultToolCategories?: string[];
  pluginSlug?: string;
}

export interface ProfileQuota {
  allowed: boolean;
  reason?: string;
  maxDailyTokens: number | null;
  dailyTokensUsed: number;
  maxDailyConversations: number | null;
  dailyConversationsUsed: number;
  quotaResetAt: string | null;
}

// --- Permission Decisions (F12 Sprint B) ---

export type DecisionLayer = 'profile' | 'plugin' | 'rbac' | 'argument' | 'quota';

export interface PermissionDecision {
  id: number;
  agentId: number | null;
  profileId: number | null;
  toolSlug: string;
  decision: 'allow' | 'deny';
  layer: DecisionLayer;
  reason: string | null;
  context: Record<string, unknown> | null;
  createdAt: string;
}

export interface PermissionStats {
  range: '24h' | '7d' | '30d';
  totalDecisions: number;
  allowCount: number;
  denyCount: number;
  layerBreakdown: Record<DecisionLayer, { allow: number; deny: number }>;
  topDeniedTools: Array<{ toolSlug: string; count: number }>;
  topDeniedReasons: Array<{ reason: string; count: number }>;
}

// --- Artifact types (F12 Sprint D) ---

export type ArtifactKind =
  | 'output'
  | 'reasoning'
  | 'tool_call'
  | 'artifact_file'
  | string;

export interface Artifact {
  id: number;
  agentId: number;
  runId: string | null;
  kind: ArtifactKind;
  content: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  expiresAt: string | null;
}

export interface ArtifactFilter {
  kind?: string;
  runId?: string;
  pinned?: boolean;
  limit?: number;
  offset?: number;
}

export interface ArtifactListResponse {
  items: Artifact[];
  total: number;
}

// --- Agent Bindings (F15 Sprint B) ---

export interface AgentBindingPosition {
  slot: 'sidebar' | 'main' | 'inline';
  priority: number;
}

export interface AgentBinding {
  id: number;
  agentId: number;
  targetType: 'module' | 'page' | 'widget';
  targetId: string;
  position: AgentBindingPosition | null;
  enabled: boolean;
  metadata: Record<string, unknown> | null;
  createdBy: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBindingBody {
  targetType: 'module' | 'page' | 'widget';
  targetId: string;
  position?: AgentBindingPosition;
  enabled?: boolean;
  metadata?: Record<string, unknown>;
}

// --- AI Triggers (F15 Sprint C) ---

export type TriggerType = 'cron' | 'event' | 'webhook' | 'schedule';
export type TriggerTargetType = 'agent' | 'pipeline' | 'tool' | 'gateway_endpoint';
export type TriggerFireStatus = 'success' | 'error' | 'skipped';

export interface CronTriggerConfig {
  expression: string;
  timezone?: string;
}

export interface EventTriggerConfig {
  topic: string;
  filter?: Record<string, unknown>;
}

export interface WebhookTriggerConfig {
  secret_hash: string;
  allowed_ips?: string[];
}

export interface ScheduleTriggerConfig {
  at: string;
  recurring?: 'daily' | 'weekly' | 'monthly';
}

export type TriggerConfig =
  | CronTriggerConfig
  | EventTriggerConfig
  | WebhookTriggerConfig
  | ScheduleTriggerConfig;

export interface AiTrigger {
  id: number;
  name: string;
  slug: string;
  type: TriggerType;
  config: TriggerConfig;
  targetType: TriggerTargetType;
  targetId: number;
  enabled: boolean;
  lastFiredAt: string | null;
  lastFireStatus: TriggerFireStatus | null;
  lastError: string | null;
  fireCount: number;
  metadata: Record<string, unknown> | null;
  createdBy: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTriggerBody {
  name: string;
  slug: string;
  type: TriggerType;
  config: TriggerConfig;
  targetType: TriggerTargetType;
  targetId: number;
  enabled?: boolean;
  metadata?: Record<string, unknown>;
}

export type UpdateTriggerBody = Partial<CreateTriggerBody>;

export interface TriggerListFilter {
  type?: TriggerType;
  enabled?: boolean;
  targetType?: TriggerTargetType;
}

export const triggersService = {
  async list(filter: TriggerListFilter = {}): Promise<{ items: AiTrigger[]; total: number }> {
    const params = new URLSearchParams();
    if (filter.type !== undefined) params.set('type', filter.type);
    if (filter.enabled !== undefined) params.set('enabled', String(filter.enabled));
    if (filter.targetType !== undefined) params.set('targetType', filter.targetType);
    const qs = params.toString();
    const { data } = await api.get<ApiResponse<{ items: AiTrigger[]; total: number }>>(
      `/admin/triggers${qs ? `?${qs}` : ''}`,
    );
    return data.data!;
  },

  async get(id: number): Promise<AiTrigger> {
    const { data } = await api.get<ApiResponse<AiTrigger>>(`/admin/triggers/${id}`);
    return data.data!;
  },

  async create(body: CreateTriggerBody): Promise<{ trigger: AiTrigger; webhookSecret?: string }> {
    const { data } = await api.post<ApiResponse<{ trigger: AiTrigger; webhookSecret?: string }>>(
      '/admin/triggers',
      body,
    );
    return data.data!;
  },

  async update(id: number, body: UpdateTriggerBody): Promise<AiTrigger> {
    const { data } = await api.patch<ApiResponse<AiTrigger>>(`/admin/triggers/${id}`, body);
    return data.data!;
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/admin/triggers/${id}`);
  },

  async fire(id: number, payload: Record<string, unknown> = {}): Promise<{ status: TriggerFireStatus; error?: string }> {
    const { data } = await api.post<ApiResponse<{ status: TriggerFireStatus; error?: string }>>(
      `/admin/triggers/${id}/fire`,
      payload,
    );
    return data.data!;
  },
};
